import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { dashboardContentService } from '../../../services/dashboardContentService';
import PostOfWeekEditModal from './PostOfWeekEditModal';
import ReadyToShareEditModal from './ReadyToShareEditModal';

const DashboardContentPanel = () => {
  const [postOfWeek, setPostOfWeek] = useState(null);
  const [readyToShareItems, setReadyToShareItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingPost, setEditingPost] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const [post, items] = await Promise.all([
        dashboardContentService?.getPostOfWeek(),
        dashboardContentService?.getReadyToShareItems()
      ]);
      setPostOfWeek(post);
      setReadyToShareItems(items);
      setError('');
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await dashboardContentService?.deletePostOfWeek(id);
      loadContent();
    } catch (err) {
      alert('Failed to delete: ' + err?.message);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await dashboardContentService?.deleteReadyToShareItem(id);
      loadContent();
    } catch (err) {
      alert('Failed to delete: ' + err?.message);
    }
  };

  const handlePostSuccess = () => {
    setShowPostModal(false);
    setEditingPost(null);
    loadContent();
  };

  const handleItemSuccess = () => {
    setShowItemModal(false);
    setEditingItem(null);
    loadContent();
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-semibold text-foreground">Dashboard Content Management</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage Posts of the Week and Ready to Share content
        </p>
      </div>

      <div className="p-6 space-y-8">
        {/* Post of the Week Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="Star" size={20} className="text-primary" />
              Post of the Week
            </h3>
            <Button
              onClick={() => {
                setEditingPost(null);
                setShowPostModal(true);
              }}
              size="sm"
              iconName="Plus"
              iconPosition="left"
            >
              Add New Post
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader" size={24} className="animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">{error}</div>
          ) : postOfWeek ? (
            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex gap-4">
                <img
                  src={postOfWeek?.image}
                  alt={postOfWeek?.imageAlt}
                  className="w-32 h-32 object-cover rounded"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-1">{postOfWeek?.title}</h4>
                  <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                    {postOfWeek?.caption}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                    <span className="bg-primary/10 text-primary px-2 py-1 rounded">
                      {postOfWeek?.platform}
                    </span>
                    <span>{postOfWeek?.hashtags?.length} hashtags</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingPost(postOfWeek);
                        setShowPostModal(true);
                      }}
                      iconName="Edit"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeletePost(postOfWeek?.id)}
                      iconName="Trash2"
                      className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No post configured. Click "Add New Post" to create one.
            </div>
          )}
        </div>

        {/* Ready to Share Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="Share2" size={20} className="text-primary" />
              Ready to Share Items
            </h3>
            <Button
              onClick={() => {
                setEditingItem(null);
                setShowItemModal(true);
              }}
              size="sm"
              iconName="Plus"
              iconPosition="left"
            >
              Add New Item
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader" size={24} className="animate-spin text-primary" />
            </div>
          ) : readyToShareItems?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No items configured. Click "Add New Item" to create one.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {readyToShareItems?.map((item) => (
                <div
                  key={item?.id}
                  className="bg-background border border-border rounded-lg overflow-hidden"
                >
                  <img
                    src={item?.image}
                    alt={item?.imageAlt}
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-3">
                    <h4 className="font-semibold text-foreground text-sm mb-1 line-clamp-1">
                      {item?.title}
                    </h4>
                    <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                      {item?.description}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                      <span className="bg-muted px-2 py-1 rounded">{item?.contentType}</span>
                      <span>{item?.date}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingItem(item);
                          setShowItemModal(true);
                        }}
                        iconName="Edit"
                        fullWidth
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteItem(item?.id)}
                        iconName="Trash2"
                        className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showPostModal && (
        <PostOfWeekEditModal
          post={editingPost}
          onClose={() => {
            setShowPostModal(false);
            setEditingPost(null);
          }}
          onSuccess={handlePostSuccess}
        />
      )}
      {showItemModal && (
        <ReadyToShareEditModal
          item={editingItem}
          onClose={() => {
            setShowItemModal(false);
            setEditingItem(null);
          }}
          onSuccess={handleItemSuccess}
        />
      )}
    </div>
  );
};

export default DashboardContentPanel;